import clsx from 'clsx'
import React from 'react'
import { useTranslation } from 'react-i18next'

import Banner from '@/components-latest/primitives/Banner'
import ContentBlock from '@/components-latest/primitives/ContentBlock'
import Store from '@/components-latest/primitives/Icons/Store'
import { LoadingSpinner } from '@/components-latest/primitives/Spinner'
import Text from '@/components-latest/primitives/Text'
import RevenueBar from '@/components-latest/ui/PerformancePage/RevenueBar'
import { IRevenuePerformanceData } from '@/ducks/performance/performance.schema'
import {
  getTotalPayout,
  hasPayoutFields,
  hasReimbursedCancelledInfo,
  hasReturnedEarningsBreakdown,
  hasTaxStampInfo,
  parseRevenueAmount,
  REVENUE_LEGACY_PRICE_OPTIONS,
  REVENUE_PAYOUT_PRICE_OPTIONS,
  shouldShowInProgressNotice
} from '@/ducks/performance/utils/revenuePayout.utils'
import { formatPrice } from '@/utils/currency.utils'

import Styles from './RevenuePerformance.module.scss'

type Props = {
  data?: IRevenuePerformanceData[]
  isLoading?: boolean
  timeframe: 'hours' | 'days'
  multiple?: boolean
}

const formatRevenueAmount = (
  value: number,
  currency: string,
  usePayoutPrecision: boolean,
  options?: { signDisplay?: 'never' | 'always' | 'auto' }
) =>
  formatPrice(
    value,
    currency,
    usePayoutPrecision
      ? { ...REVENUE_PAYOUT_PRICE_OPTIONS, ...options }
      : { ...REVENUE_LEGACY_PRICE_OPTIONS, ...options }
  )

const RevenuePerformance = React.memo(
  ({ isLoading, data = [], timeframe = 'hours', multiple = false }: Props) => {
    const { t } = useTranslation()

    if (isLoading) {
      return (
        <ContentBlock>
          <div className={Styles.spinner}>
            <LoadingSpinner
              containerType="centered"
              text={t('performance.loading.revenue')}
            />
          </div>
        </ContentBlock>
      )
    }

    if (!data.length) {
      return (
        <ContentBlock>
          <Text asChild>
            <p>{t('performance.revenue.no-data')}</p>
          </Text>
        </ContentBlock>
      )
    }

    return (
      <ContentBlock>
        <Text variant="nextTitle-2" weight="extraBold" asChild>
          <p>{t('performance.revenue.title')}</p>
        </Text>

        {data.map((store, idx) => {
          const currency = store?.currency || 'DZD'
          const usePayoutDisplay = hasPayoutFields(store)
          const showBreakdown = hasReturnedEarningsBreakdown(store)
          const totalPayout = getTotalPayout(store)
          const headlineLabel = usePayoutDisplay
            ? t('performance.revenue.total-payout')
            : t('performance.revenue.net-profit')

          const secondaryInfo = [
            {
              id: 'gross',
              value: formatRevenueAmount(
                parseRevenueAmount(store?.grossEarning),
                currency,
                usePayoutDisplay,
                { signDisplay: 'always' }
              ),
              text: t('performance.revenue.gross-earning')
            },
            {
              id: 'comission',
              value: `-${formatRevenueAmount(
                parseRevenueAmount(store?.comission),
                currency,
                usePayoutDisplay
              )}`,
              text: t('performance.revenue.yassir-comission')
            }
          ]

          return (
            <div key={idx} className={Styles.content}>
              {shouldShowInProgressNotice(store) && (
                <Banner
                  className={Styles.notice}
                  state="info"
                  type="plain"
                  description={t('performance.revenue.cycle-in-progress')}
                />
              )}

              <div
                className={
                  usePayoutDisplay ? Styles.payoutSection : Styles.info
                }>
                {multiple && store?.area && (
                  <div className={Styles.infoItem}>
                    <div className={Styles.row}>
                      <Store />
                      <Text
                        variant="subtitle"
                        className={Styles.comission}
                        asChild>
                        <p>{store?.area}</p>
                      </Text>
                    </div>
                  </div>
                )}

                {usePayoutDisplay ? (
                  <>
                    <div className={Styles.payoutHeadline}>
                      <Text
                        variant="nextTitle-1"
                        weight="extraBold"
                        className={Styles.profit}
                        asChild>
                        <p>
                          {formatRevenueAmount(totalPayout, currency, true)}
                        </p>
                      </Text>
                      <Text
                        className={Styles.infoText}
                        variant="nextBody"
                        asChild>
                        <p>{headlineLabel}</p>
                      </Text>
                    </div>

                    {showBreakdown && (
                      <div className={Styles.breakdown}>
                        <div className={Styles.breakdownRow}>
                          <Text variant="nextBody" asChild>
                            <p>{t('performance.revenue.net-earnings')}</p>
                          </Text>
                          <Text variant="nextBody" weight="semiBold" asChild>
                            <p>
                              {formatRevenueAmount(
                                parseRevenueAmount(store.netEarning),
                                currency,
                                true
                              )}
                            </p>
                          </Text>
                        </div>
                        <div className={Styles.breakdownRow}>
                          <Text variant="nextBody" asChild>
                            <p>{t('performance.revenue.returned-earnings')}</p>
                          </Text>
                          <Text variant="nextBody" weight="semiBold" asChild>
                            <p>
                              {formatRevenueAmount(
                                parseRevenueAmount(store.returned_earnings),
                                currency,
                                true,
                                { signDisplay: 'always' }
                              )}
                            </p>
                          </Text>
                        </div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className={Styles.infoItem}>
                    <Text
                      variant="nextTitle-1"
                      weight="extraBold"
                      className={Styles.profit}
                      asChild>
                      <p>{formatRevenueAmount(totalPayout, currency, false)}</p>
                    </Text>
                    <Text
                      className={Styles.infoText}
                      variant="nextBody"
                      asChild>
                      <p>{headlineLabel}</p>
                    </Text>
                  </div>
                )}

                {!usePayoutDisplay &&
                  secondaryInfo.map(({ id, value, text }) => (
                    <div key={id} className={Styles.infoItem}>
                      <Text
                        variant="nextTitle-1"
                        weight="extraBold"
                        className={Styles[id]}
                        asChild>
                        <p>{value}</p>
                      </Text>
                      <Text
                        className={Styles.infoText}
                        variant="nextBody"
                        asChild>
                        <p>{text}</p>
                      </Text>
                    </div>
                  ))}
              </div>

              {usePayoutDisplay && (
                <div className={clsx(Styles.info, Styles.infoSecondary)}>
                  {secondaryInfo.map(({ id, value, text }) => (
                    <div key={id} className={Styles.infoItem}>
                      <Text
                        variant="nextTitle-1"
                        weight="extraBold"
                        className={Styles[id]}
                        asChild>
                        <p>{value}</p>
                      </Text>
                      <Text
                        className={Styles.infoText}
                        variant="nextBody"
                        asChild>
                        <p>{text}</p>
                      </Text>
                    </div>
                  ))}
                </div>
              )}

              {hasTaxStampInfo(store) && (
                <Text
                  className={Styles.infoFootnote}
                  variant="nextBody"
                  asChild>
                  <p>
                    {t('performance.revenue.tax-stamp-deducted', {
                      amount: `-${formatRevenueAmount(
                        parseRevenueAmount(store.tax_stamp_deductions),
                        currency,
                        true
                      )}`,
                      perCycle: formatRevenueAmount(
                        parseRevenueAmount(store.tax_stamp_per_cycle),
                        currency,
                        true
                      ),
                      cycles: store.cycles_in_window ?? 0
                    })}
                  </p>
                </Text>
              )}

              {hasReimbursedCancelledInfo(store) && store.reimbursed_cancelled && (
                <Text
                  className={Styles.infoFootnote}
                  variant="nextBody"
                  asChild>
                  <p>
                    {t('performance.revenue.reimbursed-cancelled', {
                      count: store.reimbursed_cancelled.count,
                      earnings: formatRevenueAmount(
                        parseRevenueAmount(store.reimbursed_cancelled.earnings),
                        currency,
                        true
                      )
                    })}
                  </p>
                </Text>
              )}

              {!multiple && (
                <div className={Styles.chart}>
                  {store?.revenue && store.revenue.length > 0 ? (
                    <>
                      <RevenueBar
                        revenues={store.revenue}
                        timeframe={timeframe}
                      />
                      <Text variant="nextSubtitle" className={Styles.legend}>
                        {headlineLabel}
                      </Text>
                    </>
                  ) : (
                    <Text asChild>
                      <p>{t('performance.revenue.no-data')}</p>
                    </Text>
                  )}
                </div>
              )}
            </div>
          )
        })}

        {multiple && (
          <div className={Styles.chart}>
            <RevenueBar
              revenues={data.map(store => ({
                area: store?.area,
                revenue: store?.revenue || []
              }))}
              timeframe={timeframe}
              multiple
            />
          </div>
        )}
      </ContentBlock>
    )
  }
)

RevenuePerformance.displayName = 'RevenuePerformance'
export default RevenuePerformance
